import { Injectable } from '@angular/core';
import { catchError, from, map, Observable, of } from 'rxjs';
import { ChatBotFlowSaService } from 'src/app/features/chat-bot/components/ui-chat-bot-sa/chat-bot-flow-sa.service';
import { WordOfMouthChannelsStepData } from 'src/app/features/chat-bot/types/steps';
import { StepAction } from 'src/app/features/chat-bot/types/steps.type';
import { getDeviceInfo } from 'src/app/shared/utils/device-info.utils';

export interface RedirectOptions {
  url: string;
  channelType?: string;
  shareText?: string;
  useWebShare?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class WebShareApiService {
  constructor(private chatBotFlowSaService: ChatBotFlowSaService) {}

  handleRedirect(options: RedirectOptions): void {
    const { url, channelType } = options;
    if (channelType === 'logo-share-api') {
      return;
    }
    this.safeRedirect(url, channelType);
  }

  private safeRedirect(url: string, channelType?: string): void {
    const deviceInfo = this.getDeviceInfo();

    //  WhatsApp on mobile devices
    if (deviceInfo.isMobile) {
      this.chatBotFlowSaService.notifyRedirectStarted();
      window.location.href = url;
      return;
    }

    //  SMS/Email on iPad Safari (not Firefox)
    if (
      deviceInfo.isIpad &&
      !deviceInfo.isFirefox &&
      (channelType === 'logo-sms' || channelType === 'emoji-letter')
    ) {
      this.chatBotFlowSaService.notifyRedirectStarted();
      window.location.href = url;
      return;
    }

    //  Default behavior for desktop and other cases
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      this.createAndClickLink(url);
    }
  }

  // Device detection utility
  getDeviceInfo() {
    return getDeviceInfo();
  }

  //  Fallback link creation method
  private createAndClickLink(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  canUseWebShare(): boolean {
    const hasWebShare =
      typeof navigator !== 'undefined' && 'share' in navigator;
    const isSecureContext = window.isSecureContext;

    return hasWebShare && isSecureContext;
  }

  tryWebShare(url: string): Observable<WordOfMouthChannelsStepData> {
    let decodedText = decodeURIComponent(url);

    const deviceInfo = this.getDeviceInfo();
    let shareData: ShareData;
    // Use only title for Apple devices, only text for others
    if (
      deviceInfo.isIOS ||
      deviceInfo.isIpad ||
      /Macintosh|Mac OS/i.test(navigator.userAgent)
    ) {
      shareData = { title: decodedText };
    } else {
      shareData = { text: decodedText };
    }

    // The native share sheet hands the foreground to another app (WhatsApp,
    // Mail, ...). Some browsers - Samsung Internet on Android in particular -
    // fire `pagehide` for that, which must not be read as the user closing the
    // bot. `handleRedirect` short-circuits for `logo-share-api`, so this is the
    // only place the share-sheet path can arm the suppression.
    this.chatBotFlowSaService.notifyRedirectStarted();

    return from(navigator.share(shareData)).pipe(
      map(() => ({ share_status: 'SHARE' })),
      catchError((error: any) => {
        if (error.name === 'AbortError' || error.name === 'NotAllowedError') {
          // The share was dismissed or blocked, so no app ever took the
          // foreground. Disarm the suppression: the page never left, a later
          // `pagehide` is a genuine close, and a later refresh must not be
          // mistaken for the OS discarding the tab mid-share.
          this.chatBotFlowSaService.notifyPageRestored();
          return of({
            share_status: error.name === 'AbortError' ? 'CANCEL' : 'DENIED'
          });
        } else {
          return of({ share_status: 'ERROR' });
        }
      })
    );
  }

  //  Handle old funnel redirect logic
  async handleLegacyRedirect(
    redirectUrl: string,
    delay: number = 100
  ): Promise<void> {
    setTimeout(() => {
      window.open(redirectUrl, '_blank');
    }, delay);
  }

  getChannelUrlFromService(
    option: StepAction,
    currentLang: string
  ): string | null {
    const { isMobile } = getDeviceInfo();
    const womChannels = this.chatBotFlowSaService.getWomChannels();
    if (womChannels) {
      switch (option.icon) {
        case 'logo-whatsapp':
          if (womChannels.whatsapp?.[currentLang]) {
            return isMobile
              ? womChannels.whatsapp[currentLang].mobile
              : womChannels.whatsapp[currentLang].desktop;
          }
          break;

        case 'logo-sms':
          return womChannels.sms?.[currentLang] || null;

        case 'emoji-letter':
          return womChannels.email?.[currentLang] || null;
        case 'logo-share-api':
          return womChannels.share_api?.[currentLang] || null;

        default:
          console.warn('Unknown channel icon:', option.icon);
          return null;
      }
    }

    return null;
  }
}
